import { useState, type ReactNode } from 'react'

import type { TransportOptions, TransportService } from '../../api/transport'
import { Button } from '../../components/Button'
import { PageBand, PageShell } from '../../components/PageShell'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { useBooking } from './booking'
import { Desk } from './Desk'
import { useFocusOnMount } from './focus'
import { ProgressBar } from './ProgressBar'
import {
  minimumNotice,
  SERVICE_DESCRIPTION,
  SERVICE_NAME,
  serviceCard,
  SERVICES,
  startingPrice,
  type Card,
} from './services'
import { TransportHeader } from './TransportHeader'

/**
 * Screen 2, choose a service (M39-T10, spec §C.3.2). All four cards, always:
 * the recommended one labelled, the unsuitable ones disabled with the server's
 * reason, an unpriced one sent to the desk. Each card is a radio in one
 * labelled group, so arrow keys move between the ones that can be chosen.
 */
/** What Screen 2 says when it is shown for a reason: the lead, then the
 * refused card's reason in words. */
export type ServiceNotice = { lead: string; reason: string | null }

export function ServiceScreen({
  options,
  onBack,
  onStop,
  onContinue,
  notice = null,
}: {
  options: TransportOptions
  onBack: () => void
  /** 112 from this screen ends the booking (not counted: only the gate is). */
  onStop: () => void
  onContinue: () => void
  /** Why the person was sent back here (M39-T12: an edited answer ruled
   * out the service they had chosen; M40-T11: the service of a trip booked
   * again is not open for the new answers). */
  notice?: ServiceNotice | null
}): ReactNode {
  const { booking, update } = useBooking()
  const heading = useFocusOnMount<HTMLHeadingElement>()
  const row = options.eligibility.find(
    (r) =>
      r.mobility === booking.mobility && r.uses_oxygen === booking.usesOxygen,
  )
  const cards = SERVICES.map((service) => ({
    service,
    card: serviceCard(service, row, options),
  }))
  // Screen 1 clears the choice whenever its answers change, so a kept choice
  // is still one these answers allow.
  const [chosen, setChosen] = useState<TransportService | null>(booking.service)

  return (
    <PageShell measure={640} as="main">
      <TransportHeader onBack={onBack} />
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        <h1
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          Choose a service
        </h1>
        <ProgressBar current={1} />
        {notice && (
          <p
            role="alert"
            className="rounded-r3 border-l-[3px] border-sos bg-sos-bg px-3 py-2.5 text-[12.5px] leading-relaxed text-ink-1"
          >
            {notice.lead}
            {notice.reason ? ` ${notice.reason}` : ''}
          </p>
        )}

        <fieldset>
          <legend className="font-mono text-[10px] uppercase tracking-wide text-ink-3">
            Which service?
          </legend>
          <div className="mt-2 flex flex-col gap-2">
            {cards.map(({ service, card }) => (
              <ServiceCard
                key={service}
                service={service}
                card={card}
                options={options}
                checked={chosen === service}
                onChoose={() => setChosen(service)}
              />
            ))}
          </div>
        </fieldset>

        <p className="text-[12px] leading-relaxed text-ink-3">
          Fixed fares by zone (Tema, Accra, Outer Greater Accra). You&apos;ll
          see the exact fare before you pay.
        </p>

        <Button
          kind="primary"
          full
          disabled={chosen === null}
          onClick={() => {
            // Car Hire's trip has a different shape: a new service starts
            // Screen 3 afresh.
            update({
              service: chosen,
              ...(chosen !== booking.service ? { trip: null } : {}),
            })
            onContinue()
          }}
        >
          {chosen ? `Continue with ${SERVICE_NAME[chosen]}` : 'Continue'}
        </Button>

        <p className="rounded-r3 border-l-[3px] border-sos bg-sos-bg px-3 py-2.5 text-[12px] leading-relaxed text-ink-1">
          Medical emergency? Call{' '}
          <a
            href={`tel:${EMERGENCY_NUMBER}`}
            onClick={onStop}
            onAuxClick={onStop}
            className="font-semibold text-sos underline underline-offset-2"
          >
            {EMERGENCY_NUMBER}
          </a>{' '}
          — do not wait for a booking.
        </p>
      </PageBand>
    </PageShell>
  )
}

function ServiceCard({
  service,
  card,
  options,
  checked,
  onChoose,
}: {
  service: TransportService
  card: Card
  options: TransportOptions
  checked: boolean
  onChoose: () => void
}): ReactNode {
  const selectable = card.kind === 'open'
  // On every card the server prices, chosen or not (a refused card still
  // says what the service costs; an unpriced one has no `from_price`).
  const price = startingPrice(service, options)
  const notice = minimumNotice(service, options)
  const noteId = `service-${service}-note`
  const note = cardNote(card)

  return (
    <div
      className={`rounded-r3 border px-3 py-2.5 ${
        checked
          ? 'border-accent bg-accent-bg'
          : selectable
            ? 'border-line-2 bg-bg-app'
            : 'border-line-1 bg-bg-soft'
      }`}
    >
      <label
        className={`flex items-start gap-2.5 ${selectable ? '' : 'text-ink-3'}`}
      >
        <input
          type="radio"
          name="service"
          value={service}
          checked={checked}
          disabled={!selectable}
          onChange={onChoose}
          aria-describedby={note ? noteId : undefined}
          className="mt-0.5 h-4 w-4 shrink-0 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span
              className={`text-[14px] font-semibold ${selectable ? 'text-ink-1' : 'text-ink-2'}`}
            >
              {SERVICE_NAME[service]}
            </span>
            {card.kind === 'open' && card.recommended && (
              <span className="rounded-full border border-accent px-1.5 py-px text-[10.5px] font-semibold text-accent">
                Recommended
              </span>
            )}
          </span>
          <span className="mt-0.5 block text-[12px] leading-relaxed text-ink-2">
            {SERVICE_DESCRIPTION[service]}
          </span>
          {(price || notice) && (
            <span className="mt-1 block text-[12px] text-ink-2">
              {price && (
                <span className="font-medium tabular-nums text-ink-1">
                  {price}
                </span>
              )}
              {price && notice && ' · '}
              {notice}
            </span>
          )}
        </span>
      </label>
      {note && (
        <p
          id={noteId}
          className="mt-1.5 pl-[26px] text-[12px] leading-relaxed text-ink-2"
        >
          {note}
        </p>
      )}
    </div>
  )
}

/** The line under a card: why it can't be chosen, or the caregiver rule. */
function cardNote(card: Card): ReactNode {
  switch (card.kind) {
    case 'members':
      return (
        <>
          Members: call the transport desk
          <Desk /> to book.
        </>
      )
    case 'offline':
      return (
        <>
          Not available online yet — call the transport desk
          <Desk />.
        </>
      )
    case 'reason':
      return card.reason
    case 'open':
      return card.caregiver
  }
}
